import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { ButtonCenterLocation, ButtonZoom, theme, useResponsive } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────────

export const MapControls = (props: Props) => {
  const { onZoomInPress, onZoomOutPress, onGeolocateUserBtnPress, disableGeolocateUserBtn } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────────────

  const zoomInLabel = formatMessage(messages.zoomInLabel);
  const zoomOutLabel = formatMessage(messages.zoomOutLabel);
  const geolocateUserBtnLabel = formatMessage(messages.geolocateUserBtnLabel);

  // ─────────────────────────────────────────────────────────────────────────────

  const zoomProps = useMemo(
    () => ({
      zoomInProps: { onPress: onZoomInPress, accessibilityLabel: zoomInLabel },
      zoomOutProps: { onPress: onZoomOutPress, accessibilityLabel: zoomOutLabel }, // prettier-ignore
    }),
    [onZoomInPress, onZoomOutPress, zoomInLabel, zoomOutLabel],
  );

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const controlsContainerStyles = [
    styles.controlsContainer,
    match([styles.controlsContainerXS, styles.controlsContainerSM]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={controlsContainerStyles}>
      <ButtonCenterLocation
        onPress={onGeolocateUserBtnPress}
        disabled={disableGeolocateUserBtn}
        accessibilityLabel={geolocateUserBtnLabel}
      />

      <ButtonZoom zoomInProps={zoomProps.zoomInProps} zoomOutProps={zoomProps.zoomOutProps} />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  geolocateUserBtnLabel: {
    defaultMessage: 'Get current location and center map on it',
    description: 'Map Controls | Geolocate User Button | Accessibility Label',
  },
  zoomInLabel: {
    defaultMessage: 'Zoom in',
    description: 'Map Controls | Zoom In Button | Accessibility Label',
  },
  zoomOutLabel: {
    defaultMessage: 'Zoom out',
    description: 'Map Controls | Zoom Out Button | Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  controlsContainer: {
    position: 'absolute',
    zIndex: 1,
    gap: theme.spacing['4'],
  },
  controlsContainerXS: {
    top: theme.spacing['4'],
    right: theme.spacing['4'],
  },
  controlsContainerSM: {
    right: theme.spacing['6'],
    bottom: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type Props = {
  onZoomInPress?: () => void;
  onZoomOutPress?: () => void;
  onGeolocateUserBtnPress?: () => void;
  disableGeolocateUserBtn?: boolean;
};
